import styled, { keyframes, createGlobalStyle } from 'styled-components';
import { PanelContainer as Container, PanelCard as TableContainer, Title as TitleText, PrimaryButton, SecondaryButton, FilterSelect, IconBadge } from '../../ui/common';
import { PRZYPIETE_KOLUMNY_MEDIA } from './utils';

export { Container, TableContainer, PrimaryButton, SecondaryButton, FilterSelect };

export const DatepickerTheme = createGlobalStyle`
  .react-datepicker {
    width: 252px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: var(--card-bg);
    border: 1px solid var(--input-border);
    border-radius: 14px;
    box-shadow: 0 10px 34px var(--shadow-color);
    overflow: hidden;
    padding: 0 0 6px 0;
  }
  .react-datepicker__month { margin: 4px 6px 0 6px; }

  .react-datepicker__triangle { display: none; }

  .react-datepicker-popper[data-placement^="bottom"],
  .react-datepicker-popper[data-placement^="top"] {
    margin-top: 6px;
    z-index: var(--z-popover) !important; /* nad dropdownami filtrów */
  }

  .react-datepicker__header {
    background: var(--primary);
    border-bottom: 1px solid rgba(255,255,255,0.15);
    padding-top: 6px;
    padding-bottom: 4px;
  }

  .react-datepicker__current-month {
    color: var(--primary-contrast);
    font-weight: 600;
    font-size: 0.90rem;
    letter-spacing: .3px;
  }

  .react-datepicker__day-names { margin-top: 2px; }
  .react-datepicker__day-name {
    color: rgba(255,255,255,0.85);
    font-weight: 500;
    width: 32px;
    line-height: 32px;
    margin: 1px;
    font-size: 0.65rem;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day {
    width: 32px;
    height: 32px;
    line-height: 32px;
    margin: 1px;
    border-radius: 8px;
    font-weight: 500;
    font-size: 0.78rem;
    color: var(--text);
    transition: all .15s ease;
    outline: none;
  }

  .react-datepicker__day:hover {
    background: var(--table-row-hover);
    cursor: pointer;
  }

  .react-datepicker__day--outside-month { opacity: 0.30; }

  .react-datepicker__day--today:not(.react-datepicker__day--selected):not(.react-datepicker__day--in-range) {
    font-weight: 600;
    box-shadow: inset 0 0 0 2px rgba(59,130,246,0.45);
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background: var(--primary);
    color: var(--primary-contrast) !important;
    box-shadow: var(--shadow-primary);
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range {
    background: rgba(59,130,246,0.18);
    color: var(--text);
    border-radius: 8px;
  }

  .react-datepicker__navigation { top: 8px; }
  .react-datepicker__navigation-icon::before { border-color: var(--primary-contrast); }

  [data-theme='dark'] .react-datepicker {
    background: var(--panel-bg);
  }
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;

  &[data-status="dostępny"],
  &[data-status="available"] {
    background: var(--success-bg);
    color: var(--success);
  }

  &[data-status="wypożyczony"],
  &[data-status="rented"] {
    background: color-mix(in srgb, var(--primary) 15%, transparent);
    color: var(--primary);
  }

  &[data-status="w serwisie"],
  &[data-status="in service"] {
    background: var(--warning-bg);
    color: var(--warning);
  }

  &[data-status="nieaktywny"],
  &[data-status="inactive"] {
    background: color-mix(in srgb, var(--text-muted) 15%, transparent);
    color: var(--text-muted);
  }
`;

// Stylowany przycisk ustawień — dziedziczy animacje i efekty z PrimaryButton;
// wysokość ujednolicona z resztą CTA w nagłówku przez ActionButtons (& > button).
export const SettingsButton = styled(PrimaryButton)`
  svg { color: var(--primary-contrast); width: 16px; height: 16px; }

  @media (max-width: 768px) {
    width: 100%;
    padding: 12px 16px;
  }

  /* zachowaj delikatniejszy cień przy hover, ale użyj tych samych transformacji */
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-primary);
  }
`;

export const slideIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Container replaced by common PanelContainer

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  gap: 16px;

  @media(max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Ikona hero strony — wspólna plakietka z ui/common.js (IconBadge); ton "pine" (świerk)
// sygnalizuje temat strony (sprzęt/magazyn), patrz komentarz przy ICON_TONES w ui/common.js.
export const TitleIcon = styled(IconBadge).attrs({ $size: 48, $tone: 'pine' })``;

export const Title = TitleText;

export const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  flex-wrap: wrap;

  /* wspólne źródło prawdy o wysokości — pojedynczy przycisk (SettingsButton)
     dublujący padding/font-size PrimaryButton łatwo się rozjeżdża, więc
     wysokość wymuszamy tu, na wszystkich dzieciach naraz */
  & > button {
    height: 46px;
  }

  @media(max-width: 768px) {
    flex-direction: column;
    width: 100%;

    & > button {
      width: 100%;
      height: auto;
      padding: 12px 16px;
      font-size: 0.9rem;
      white-space: nowrap;
    }
  }
`;

// PrimaryButton from common

export const DangerButton = styled.button`
  background: var(--danger);
  color: var(--primary-contrast);
  border: none;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: var(--shadow-sm);
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }
`;

export const CalendarButton = styled.button`
  background: var(--input-bg);
  color: var(--text);
  border: 2px solid var(--input-border);
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: var(--table-row-hover);
    border-color: var(--primary);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--shadow-color);
  }

  &:active {
    transform: translateY(0);
  }
`;

/* ─── Pasek filtrów magazynu (redesign 2026-09-22) ───────────────────────

   Do tej pory filtry siedziały w modalu na cały ekran: żeby cokolwiek zawęzić, trzeba
   było przykryć tabelę, wyklikać wartości i zamknąć okno przyciskiem „Zastosuj filtry",
   który niczego nie stosował (filtry działały na żywo) — tylko zamykał modal. Po
   zamknięciu jedynym śladem po ustawieniach była liczba w odznace.

   Teraz filtry leżą NA STRONIE, nad tabelą: jedno pole szukania i rząd pigułek, który
   zawija się do kolejnych wierszy. Ustawiona pigułka nosi swoją wartość i krzyżyk, więc
   rząd pigułek JEST jednocześnie kontrolką i podsumowaniem — dlatego nie ma pod nim
   osobnego rzędu „aktywnych filtrów", który powtarzałby to samo drugi raz. */
export const FilterBar = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  padding: 14px 16px;
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FilterBarTop = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

export const FilterSearch = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 14px;
    width: 17px;
    height: 17px;
    color: var(--text-muted);
    pointer-events: none;
  }

  input {
    width: 100%;
    padding: 11px 14px 11px 41px;
    border-radius: var(--r-md);
    border: 1px solid var(--input-border);
    background: var(--input-bg);
    color: var(--text);
    font-family: inherit;
    font-size: 0.9375rem;

    &::placeholder { color: var(--text-muted); }
    &:focus {
      outline: none;
      border-color: var(--primary);
      box-shadow: var(--focus-ring);
    }
  }
`;

/* Liczba wyników stoi PRZY polu szukania, a nie w drugim końcu paska: to odpowiedź na
   to, co właśnie wpisano, więc czyta się razem z pytaniem. Cyfry tabelaryczne, żeby
   liczba nie skakała przy każdym wpisanym znaku. */
export const FilterResults = styled.div`
  flex-shrink: 0;
  font-size: 0.875rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  strong {
    color: var(--text);
    font-weight: 700;
  }
`;

/* Potwierdzenie, ze czytnik zadzialal. Bez niego skan wyglada jak samoczynne wpisanie
   sie tekstu w pole szukania — a przy ladzie trzeba wiedziec, ze urzadzenie odczytalo kod,
   a nie ze ktoś tracil klawiature. Gasnie samo po kilku sekundach. */
export const ScanBadge = styled.div`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 260px;
  padding: 5px 10px;
  border-radius: var(--r-pill);
  border: 1px solid var(--success-border);
  background: var(--success-bg);
  color: var(--success);
  font-size: 0.8125rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  svg { flex-shrink: 0; }
`;

export const FilterPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

export const PillWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: stretch;
`;

export const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 260px;
  padding: 7px 11px;
  border-radius: var(--r-pill);
  font-family: inherit;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease), background var(--dur-fast) var(--ease);

  background: ${({ $active }) => ($active ? 'var(--info-bg)' : 'var(--panel-bg)')};
  border: 1px solid ${({ $active }) => ($active ? 'var(--primary)' : 'var(--input-border)')};
  color: var(--text);

  /* Gdy filtr jest ustawiony, krzyżyk doklejony obok przejmuje prawy łuk pigułki. */
  ${({ $active }) => $active && `
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right: none;
  `}

  &:hover { border-color: var(--primary); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

  svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    color: var(--text-muted);
  }

  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

export const PillLabel = styled.span`
  white-space: nowrap;
`;

/* Wartość na pigułce przycina się wielokropkiem, a nie rozpycha paska: „Lokalizacja:
   Magazyn główny — regał B3" potrafi być dłuższa niż reszta rzędu razem wzięta. */
export const PillValue = styled.span`
  font-weight: 700;
  color: var(--primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &::before {
    content: '\00b7';
    margin-right: 6px;
    color: var(--text-muted);
    font-weight: 400;
  }
`;

export const PillClear = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px 0 4px;
  border-radius: 0 var(--r-pill) var(--r-pill) 0;
  border: 1px solid var(--primary);
  border-left: none;
  background: var(--info-bg);
  color: var(--text-muted);
  cursor: pointer;

  svg { width: 12px; height: 12px; }

  &:hover { color: var(--danger); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
`;

export const PillPopover = styled.div`
  /* Polozenie (top, left, transform) ustawia FilterPill przez useFloating: 6px pod pigulka,
     dosuniete do krawedzi panelu, ktory dymek przycina. */
  position: absolute;
  z-index: var(--z-dropdown);
  min-width: 220px;
  max-width: 320px;
  padding: 8px;
  border-radius: var(--r-md);
  border: 1px solid var(--input-border);
  background: var(--card-bg);
  box-shadow: var(--shadow-md);
`;

/* Kreska między własną kontrolką dymku (np. zakresem rozmiaru) a listą wartości, z podpisem
   mówiącym, że to druga, osobna droga wyboru. */
export const PillDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 14px 2px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text-muted);
  white-space: nowrap;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--input-border);
  }
`;

export const PillSearchInput = styled.input`
  width: 100%;
  margin-bottom: 6px;
  padding: 8px 10px;
  border-radius: var(--r-sm);
  border: 1px solid var(--input-border);
  background: var(--input-bg);
  color: var(--text);
  font-family: inherit;
  font-size: 0.8125rem;

  &:focus { outline: none; border-color: var(--primary); }
`;

export const PillOptionList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow-y: auto;
`;

export const PillOption = styled.button`
  text-align: left;
  padding: 8px 10px;
  border: none;
  border-radius: var(--r-sm);
  background: ${({ $selected }) => ($selected ? 'var(--info-bg)' : 'transparent')};
  color: ${({ $selected }) => ($selected ? 'var(--primary)' : 'var(--text)')};
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: ${({ $selected }) => ($selected ? 700 : 500)};
  cursor: pointer;

  &:hover { background: var(--hover-bg); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: -2px; }
`;

export const PillEmpty = styled.div`
  padding: 10px;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

/* „Wyczyść wszystko" pokazuje się DOPIERO, gdy jest co czyścić, i jest cichym linkiem,
   nie czerwonym klockiem: zdjęcie filtrów niczego nie niszczy, a czerwień stojąca na
   stronie na stałe (tak było do 2026-09-22) uczyła ignorować kolor ostrzeżenia. */
export const FilterClearAll = styled.button`
  margin-left: auto;
  padding: 6px 4px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover { color: var(--danger); }
  &:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; border-radius: 4px; }

  @media (max-width: 720px) { margin-left: 0; }
`;

/* Zakres dat dostępności w dymku pigułki — kalendarz renderuje się pod polem. */
export const PillDateSlot = styled.div`
  padding: 2px;

  .react-datepicker-wrapper { width: 100%; }

  /* Kalendarz stoi w dymku filtra (max 320px). Globalne 44px na dzien (index.css) dawalo 336px
     i dymek ucinal strzalke nastepnego miesiaca i niedziele, a stukniecie w to miejsce zamykalo
     dymek (przeglad galezi, 28.09.2026). Tu dzien ma 36px szerokosci przy pelnych 44px wysokosci:
     kalendarz ma 280px, a dymek 302px. Przy 320px widoczny obszar ma 296px, wiec ponizej ok.
     360px dzien zweza sie jak w index.css (ta sama formula), zeby dymek miescil sie z marginesem. */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    .react-datepicker .react-datepicker__day,
    .react-datepicker .react-datepicker__day-name {
      width: min(36px, calc((100vw - 80px) / 7 - 2px));
    }
  }
`;


export const SelectionActionBar = styled.div`
  margin: -6px 0 16px;
  padding: 10px 12px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media(max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SelectionMeta = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text);

  strong {
    color: var(--primary);
  }
`;

export const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--card-bg) 85%, transparent);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-overlay);
  border-radius: 14px;
`;

export const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid var(--input-border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;


export const Tooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-8px);
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: var(--z-popover);

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }
`;

export const ThWithTooltip = styled.th`
  background: var(--panel-bg);
  color: var(--text);
  font-weight: 700;
  padding: 16px;
  text-align: left;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid var(--input-border);
  position: relative;

  &:hover ${Tooltip} {
    opacity: 1;
  }

  @media(max-width: 1200px) {
    padding: 12px 10px;
    font-size: 0.75rem;
  }

  @media(max-width: 768px) {
    padding: 10px 8px;
    font-size: 0.7rem;
  }
`;

export const UndoNotification = styled.div`
  position: fixed;
  /* Nad przyciskiem pomocy w tym samym rogu (components/help/HelpButton.js). */
  bottom: calc(24px + var(--help-fab-space, 0px));
  right: 24px;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: var(--z-toast);
  animation: slideInUp 0.3s ease;

  @keyframes slideInUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export const UndoButton = styled(SecondaryButton)`
  padding: 8px 16px;
  font-size: 0.9rem;
  min-height: auto;
`;

export const AccordionHeader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  margin-top: 8px;
  background: var(--panel-bg);
  border: 1px solid var(--input-border);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--table-row-hover);
    border-color: var(--primary);
  }

  .title {
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--text);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .icon {
    transition: transform 0.2s ease;
    color: var(--text-muted);

    ${props => props.$expanded && `
      transform: rotate(180deg);
    `}
  }
`;










export const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FilterSectionHeader = styled.h4`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
  text-transform: none;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;

  .icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    flex-shrink: 0;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  /* Dekoracyjne ikony sekcji filtrów — bez znaczenia statusowego, więc jeden spójny kolor marki */
  .icon {
    background: var(--primary);
    color: var(--primary-contrast);
  }
`;







export const FilterSectionTitle = styled.div`
  grid-column: 1 / -1;
  margin-top: 16px;
  margin-bottom: 4px;
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const DynamicFiltersGrid = styled.div`
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media(max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media(max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DateFilterRow = styled.div`
  /* drugi wiersz, zaczynający od drugiej kolumny */
  grid-column: 2 / -1;
  grid-row: 2 / 3;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: center;
  margin-top: 0;
  align-self: center;
  position: relative;

  @media(max-width: 1200px) {
    grid-column: 1 / -1;
    grid-row: auto;
  }

  @media(max-width: 768px) {
    grid-column: 1 / -1;
    grid-row: auto;
  }
`;


// helper for date inputs is rendered inline as an overlay (see usage)

export const SearchInput = styled.input`
  width: 100%;
  padding: 12px 14px 12px 36px;
  min-height: 48px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  transition: all 0.2s ease;
  box-sizing: border-box;
  color: var(--text);

  &:focus {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }

  &[type='date'][value=''] {
    color: transparent;
  }

  &[type='date'][value='']::-webkit-datetime-edit {
    color: transparent;
  }

  &[type='date']::-webkit-calendar-picker-indicator {
    opacity: 1;
    cursor: pointer;
  }
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: 14px;
  color: var(--text-muted);
  pointer-events: none;
`;

// FilterSelect from common

// Wrapper dla TableContainer z horizontal scroll
export const TableWrapper = styled.div`
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  -webkit-overflow-scrolling: touch; /* smooth scrolling na iOS */

  /* PRZYPIETE PIERWSZE DWIE KOLUMNY NA EKRANACH DOTYKOWYCH (V.6)

     Zmierzone przy 390px: tabela ma 2545px i 21 kolumn, a widac 284px, wiec 89% jest poza
     ekranem. Samo przewijanie dziala, ale po przesunieciu o trzy ekrany widac np. kolumne
     „kaucja" i NIE WIADOMO, ktorego sprzetu dotyczy. Problemem szerokiej tabeli nie jest wiec
     przewijanie, tylko UTRATA KONTEKSTU.

     Przypinamy kolumne zaznaczenia (40px) i pierwsza kolumne z danymi. Zadna kolumna nie
     znika i przewijanie dziala dokladnie jak dotad — zmienia sie tylko to, ze wiadomo, czyj
     jest wiersz. Cien na prawej krawedzi przypietej kolumny mowi, ze tresc jedzie pod spodem,
     co jest zarazem podpowiedzia, ze tabela ma ciag dalszy.

     Tlo MUSI byc nieprzezroczyste, inaczej przewijana tresc przeswituje pod przypieta kolumna.

     Warunek to nie sama szerokosc, tylko DOTYK: tablet w poziomie ma 1024px, wiec przy samym
     max-width dostawalby wersje bez przypiec, chociaz palcem przewija sie tam tak samo jak na
     telefonie. Laptop o tej samej szerokosci okna ma kursor, wiec go to nie dotyczy.

     Przy tym samym warunku tabela stawia kolumne nazwy na poczatku (utils.js
     #kolumnyZNazwaNaPoczatku), wiec przypieta jest nazwa, nie Kategoria. Warunek jest jeden
     dla CSS i dla kodu: PRZYPIETE_KOLUMNY_MEDIA. */
  @media ${PRZYPIETE_KOLUMNY_MEDIA} {
    th:nth-child(1),
    td:nth-child(1) {
      position: sticky;
      left: 0;
      z-index: 3;
      background: var(--card-bg);
    }

    th:nth-child(2),
    td:nth-child(2) {
      position: sticky;
      left: 40px;
      z-index: 3;
      background: var(--card-bg);
      box-shadow: 6px 0 8px -6px var(--shadow-color);
    }
  }

  /* Stylizacja scrollbara */
  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: var(--input-bg);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--input-border);
    border-radius: 4px;

    &:hover {
      background: var(--text-muted);
    }
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 900px; /* stała szerokość - wymusza scroll zamiast ukrywania kolumn */

  @media(max-width: 768px) {
    font-size: 0.85rem;
  }
`;

export const Th = styled.th`
  background: var(--panel-bg);
  color: var(--text);
  font-weight: 700;
  padding: 16px;
  text-align: left;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 2px solid var(--input-border);

  @media(max-width: 1200px) {
    padding: 12px 10px;
    font-size: 0.75rem;
  }

  @media(max-width: 768px) {
    padding: 10px 8px;
    font-size: 0.7rem;
  }
`;

export const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid var(--input-border);
  color: var(--text);
  vertical-align: middle;
  font-weight: 500;

  @media(max-width: 1200px) {
    padding: 10px 8px;
    font-size: 0.85rem;
  }

  @media(max-width: 768px) {
    padding: 8px 6px;
    font-size: 0.8rem;
  }
`;

export const EquipmentTableRow = styled.tr`
  background: ${({ selected }) => selected ? 'rgba(59,130,246,0.10)' : 'transparent'};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: var(--table-row-hover);
    transform: translateY(-1px);
  }
`;

export const EquipmentCheckbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary);
  cursor: pointer;
  pointer-events: none;
`;

export const SelectionCell = styled(Td)`
  width: 48px;
  text-align: center;
  padding: 8px;
`;

export const SelectionToggleButton = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 9px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.12s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.12);
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
`;

export const SortButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: var(--text);
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  margin-left: 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: 0.7;

  &:hover {
    background: rgba(59, 130, 246, 0.15);
    transform: scale(1.1);
    opacity: 1;
    color: var(--primary);
  }

  &[data-active="true"] {
    opacity: 1;
    color: var(--primary);
    background: color-mix(in srgb, var(--primary) 10%, transparent);
  }
`;

export const OptionMenu = styled.div`
  position: absolute;
  top: 36px;
  right: 0;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 16px 64px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1);
  z-index: var(--z-popover);
  min-width: 160px;
  overflow: hidden;
  backdrop-filter: blur(10px);
`;

export const OptionMenuItem = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 12px 16px;
  text-align: left;
  cursor: pointer;
  font-size: 0.95rem;
  color: var(--text);
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--table-row-hover);
    color: var(--primary);
  }
`;



export const EmptyState = styled.div`
  text-align: center;
  padding: 64px 24px;
  color: var(--text-muted);

  .icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.7;
    color: var(--text-muted);
  }

  .text {
    font-size: 1.1rem;
    font-weight: 500;
  }
`;

export const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  align-self: center;
`;

export const FilterDropdownButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  min-height: 48px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  line-height: 1.2;
  transition: border-color .2s, box-shadow .2s, background .2s;
  gap: 8px;

  &:hover {
    border-color: var(--primary);
  }
  &:focus-visible {
    outline: none;
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }
  &[data-open="true"] {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);

    svg {
      color: var(--primary);
    }
  }
  &[data-placeholder="true"] {
    span:first-child {
      color: var(--text-muted);
      font-weight: 400;
    }
  }

  svg {
    flex-shrink: 0;
    transition: color .2s;
    color: var(--text-muted);
  }
`;

export const FilterOptionsList = styled.ul`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  max-height: 200px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-popover);
`;

export const FilterSearchInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--input-border);
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  margin-bottom: 6px;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: var(--focus-ring);
  }
`;

export const FilterOptionItem = styled.li`
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;

  .option-content {
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      color: var(--primary);
      flex-shrink: 0;
    }

    .option-label {
      flex: 1;
    }
  }

  &:hover {
    background: var(--table-row-hover);
  }

  &[data-selected="true"] {
    background: color-mix(in srgb, var(--primary) 15%, transparent);
    color: var(--primary);
    font-weight: 600;
  }
`;

export const ClearFiltersButton = styled(SecondaryButton)`
        min-height: 44px;
        min-width: 130px;
        padding: 12px 18px;
        font-size: 0.95rem;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        border-radius: 12px;
        transition: all 0.18s ease;
        background: var(--danger);
        color: var(--primary-contrast);
        border: none;
        box-shadow: var(--shadow-md);

        svg { color: var(--primary-contrast); }

        &:hover {
          transform: scale(1.05);
          box-shadow: var(--shadow-md);
          filter: brightness(1.02);
          /* zachowaj gradient przy hover — zapobiega nadpisaniu białym tłem */
          background: var(--danger);
        }

        &:hover:not(:disabled), &:focus:not(:disabled) {
          background: var(--danger);
          color: var(--primary-contrast);
        }

        &:active {
          transform: scale(0.95);
        }
`;

export const RangeInputContainer = styled.div`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--input-border);
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  background: var(--input-bg);
  color: var(--text);
  box-sizing: border-box;
  transition: all 0.2s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 48px;

  &:hover {
    border-color: var(--primary);
  }

  &:focus-within {
    border-color: var(--primary);
    background: var(--card-bg);
    box-shadow: var(--focus-ring);
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: var(--text);
    font-size: 0.95rem;
    font-weight: 500;
    padding: 0;
    height: 100%;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  input::placeholder {
    color: var(--text-muted);
    font-weight: 400;
  }

  .range-sep {
    color: var(--text-muted);
    font-weight: 700;
    flex-shrink: 0;
  }
`;


export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCard = styled.div`
  background: var(--card-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s ease;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    transform: ${props => props.$clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: ${props => props.$clickable ? '0 6px 20px var(--shadow-color)' : 'none'};
    border-color: ${props => props.$clickable ? 'var(--primary)' : 'var(--input-border)'};
  }
`;

export const StatIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.$tone ? `var(--${p.$tone}-bg)` : 'var(--info-bg)'};
  color: ${(p) => p.$tone ? `var(--${p.$tone})` : 'var(--primary)'};
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`;


export const StatContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1;
  margin-bottom: 2px;
`;

export const StatLabel = styled.div`
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--text-muted);
`;

export const ErrorBanner = styled.div`
  background: var(--danger-bg);
  border: 1px solid var(--danger-border);
  color: var(--danger);
  padding: 12px 16px;
  border-radius: 12px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ErrorMsg = styled.div`
  font-size: 0.95rem;
  line-height: 1.3;
`;

export const DismissButton = styled.button`
  background: transparent;
  border: none;
  color: var(--danger);
  cursor: pointer;
  font-weight: 700;
  padding: 6px 8px;
  border-radius: 8px;
  &:hover { background: color-mix(in srgb, var(--danger) 8%, transparent); }
`;

export const SizeFilterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

/* Zakres rozmiaru w dymku pigułki (25.10.2026): podpis, potem „od – do” w jednym rzędzie. */
export const RangeBlock = styled.div`
  padding: 4px 2px 2px;
`;

export const RangeCaption = styled.div`
  margin-bottom: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--text-muted);
`;

export const RangeInputsRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: end;
  column-gap: 10px;
`;

export const RangeField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const RangeFieldLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
`;

/* Myślnik wyrównany do środka pola, nie do podpisu: pola mają 40px, więc 20px od dołu. */
export const RangeSeparator = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  color: var(--text-muted);
  font-size: 0.95rem;
`;

export const RangeInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 10px;
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  background: var(--input-bg);
  color: var(--text);
  font-family: inherit;
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
  box-sizing: border-box;
  &:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent); }
  /* Remove number input spinners */
  -moz-appearance: textfield;
  appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
